import React, { useState } from 'react';
import InteractiveSliderCard from './InteractiveSliderCard';
import { avatarData } from './InteractiveSliderCard';
import { FilterState } from '../types/api';
import { ASSETS } from '../assets/assetMapping';

interface InteractiveCarouselProps {
  onAvatarSelect: (prompt: string, filters: Partial<FilterState>) => void;
}

export default function InteractiveCarousel({ onAvatarSelect }: InteractiveCarouselProps) {
  const [selectedCard, setSelectedCard] = useState<number>(3); // Default to the 4th card (index 3)
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const handleCardSelect = (id: number) => {
    setSelectedCard(id);
    // When clicking on an avatar, pass both prompt and filters
    const avatar = avatarData[id];
    if (avatar) {
      onAvatarSelect(avatar.prompt, avatar.filters);
    }
  };

  const handleCardHover = (id: number | null) => {
    setHoveredCard(id);
  };

  const handlePromptCopy = (prompt: string) => {
    // This is still called from InteractiveSliderCard but we handle it in handleCardSelect
    // console.log('Avatar selected with prompt:', prompt);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-center mb-4 lg:mb-6 lg:px-6">
        <div className="flex items-center justify-center gap-2 lg:gap-3">
          <div className="w-6 h-6 flex-shrink-0">
            <img 
              src={ASSETS.AI_CHAT_ICON} 
              alt="AI Chat" 
              className="w-full h-full"
            />
          </div>
          <span className="font-inter font-normal text-[14px] lg:text-[16px] text-blue-black-90 text-center">
          Click an avatar to load its prompt into the box above and edit it to create your own variation
          </span>
        </div>
      </div>

      {/* Interactive Slider */}
      <div className="w-full overflow-hidden">
        <div className="flex gap-2 lg:gap-4 animate-infinite-scroll" style={{ width: 'max-content' }}>
          {/* First set of avatars */}
          {avatarData.map((avatar, index) => (
            <div key={`first-${avatar.id}`} className="flex-shrink-0">
              <InteractiveSliderCard
                id={avatar.id}
                isSelected={selectedCard === avatar.id}
                onSelect={handleCardSelect}
                onCopyPrompt={handlePromptCopy}
                isHovered={hoveredCard === null ? undefined : hoveredCard === avatar.id}
                onHover={handleCardHover}
              />
            </div>
          ))}
          {/* Second set for seamless infinite scrolling */}
          {avatarData.map((avatar, index) => (
            <div key={`second-${avatar.id}`} className="flex-shrink-0">
              <InteractiveSliderCard
                id={avatar.id}
                isSelected={selectedCard === avatar.id}
                onSelect={handleCardSelect}
                onCopyPrompt={handlePromptCopy}
                isHovered={hoveredCard === null ? undefined : hoveredCard === avatar.id}
                onHover={handleCardHover}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="text-center mt-4 lg:px-6">
        <p className="text-sm text-blue-black-90/60">
          Click on any avatar to select it
        </p>
      </div>
    </div>
  );
}
